// Phase 6.1 — step-up auth modal for destructive user-management ops.
//
// Every privileged write (create user, reset another user's
// password, delete a user, change your own password) re-asks the
// superadmin for THEIR current password and sends it in the request
// body. This is the shared prompt; the parent supplies onConfirm,
// which performs the actual API call with the entered password.
//
// Phase 6.3.2 (B.1): chrome is now the <Modal> primitive (backdrop,
// focus trap, Esc, focus return, nested-stacking). This component
// owns only the password field + the 401 ("Incorrect password")
// retry behaviour. The password input is focused on open (Modal
// focuses its X first; this component's effect then claims focus).

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type FormEvent,
  type ReactNode,
} from "react";
import { Loader2 } from "lucide-react";

import { ApiError } from "../lib/api";
import { Modal } from "./ui/Modal";

interface StepUpAuthModalProps {
  /** Dialog heading — names the operation being authorized. */
  title: string;
  /** Optional explanatory body (what's about to happen). */
  description?: ReactNode;
  /** Confirm button label. Default "Confirm". */
  confirmLabel?: string;
  /** "danger" paints the confirm button in the error palette
   *  (used for delete). "default" is the neutral treatment. */
  confirmTone?: "default" | "danger";
  /** Performs the privileged call with the entered password.
   *  Throw ApiError(401) to surface "Incorrect password" inline;
   *  any other throw surfaces a generic inline error. Resolve to
   *  signal success — the parent then unmounts this modal. */
  onConfirm: (currentPassword: string) => Promise<void>;
  /** Cancel / Esc / backdrop. Parent unmounts the modal. */
  onClose: () => void;
}

export function StepUpAuthModal({
  title,
  description,
  confirmLabel = "Confirm",
  confirmTone = "default",
  onConfirm,
  onClose,
}: StepUpAuthModalProps) {
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Claim focus for the password input (Modal focuses its X button
  // first; this runs after, as the parent effect).
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleSubmit = useCallback(
    async (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      if (busy) return;
      if (!password) {
        setPasswordError("Enter your current password.");
        inputRef.current?.focus();
        return;
      }
      setBusy(true);
      setPasswordError(null);
      setError(null);
      try {
        await onConfirm(password);
        // Success → parent unmounts; nothing to reset here.
      } catch (err) {
        setBusy(false);
        if (err instanceof ApiError && err.status === 401) {
          setPasswordError("Incorrect password.");
          inputRef.current?.focus();
          inputRef.current?.select();
        } else if (err instanceof ApiError && err.status === 0) {
          setError("Cannot reach the service. Is it running?");
        } else if (err instanceof Error) {
          setError(err.message);
        } else {
          setError("Request failed.");
        }
      }
    },
    [busy, password, onConfirm],
  );

  const confirmClass =
    confirmTone === "danger"
      ? "border-theme-error-border text-theme-error-text hover:bg-theme-error-bg"
      : "border-theme-border-default text-theme-text-primary hover:border-theme-text-primary";

  return (
    <Modal open onClose={onClose} title={title} size="sm" busy={busy}>
      <div data-testid="stepup-modal-content">
        {description && (
          <div className="text-body-sm text-theme-text-secondary leading-relaxed mb-loose">
            {description}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-loose">
          <div>
            <label htmlFor="stepup-password" className="block text-caption mb-1.5">
              Your current password
            </label>
            <input
              ref={inputRef}
              id="stepup-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={busy}
              autoComplete="current-password"
              data-testid="stepup-password"
              aria-invalid={passwordError ? "true" : "false"}
              aria-describedby={passwordError ? "stepup-password-error" : undefined}
              className={[
                "w-full px-3 py-2 bg-theme-bg-app border font-mono text-sm",
                "text-theme-text-primary placeholder-theme-text-tertiary",
                "focus:outline-none disabled:opacity-50",
                passwordError
                  ? "border-theme-error-border focus:border-theme-error-border"
                  : "border-theme-border-default focus:border-theme-border-active",
              ].join(" ")}
            />
            {passwordError && (
              <p
                id="stepup-password-error"
                data-testid="stepup-password-error"
                className="mt-1 text-caption text-theme-error-text"
              >
                {passwordError}
              </p>
            )}
          </div>

          {error && (
            <p
              data-testid="stepup-error"
              role="alert"
              className="text-body-sm text-theme-error-text bg-theme-error-bg border border-theme-error-border px-3 py-2 leading-relaxed"
            >
              {error}
            </p>
          )}

          <div className="flex justify-end gap-snug">
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              data-testid="stepup-cancel"
              className="px-3 py-1.5 border border-theme-border-default text-caption text-theme-text-secondary hover:text-theme-text-primary hover:border-theme-border-emphasis transition-colors duration-150 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={busy}
              data-testid="stepup-confirm"
              className={`inline-flex items-center gap-tight px-3 py-1.5 border text-caption transition-colors duration-150 disabled:opacity-50 ${confirmClass}`}
            >
              {busy && (
                <Loader2 className="w-3 h-3 animate-spin" strokeWidth={1.5} aria-hidden />
              )}
              {confirmLabel}
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
}
